/**
 * Shared shapes for the 3-step sign-up wizard.
 *
 * `SignupPage` owns every piece of state and every validation rule; the three
 * step components are presentational and only read these types. The wire ->
 * step map lives here too, so mapping a server-side error back onto the step
 * that owns the field never duplicates field names across files.
 */

import type { ChangeEvent } from 'react';

/** 1-based step index. */
export type SignupStep = 1 | 2 | 3;

export const SIGNUP_STEP_TOTAL = 3;

/** Every step in order — used for the "re-check everything before posting" sweep. */
export const SIGNUP_STEPS: ReadonlyArray<SignupStep> = [1, 2, 3];

/** Short label rendered next to "STEP n OF 3". */
export const SIGNUP_STEP_LABELS: Readonly<Record<SignupStep, string>> = {
  1: 'Your details',
  2: 'Your organization',
  3: 'Terms',
};

/** Heading of each step. Also the focus target when the step changes. */
export const SIGNUP_STEP_HEADINGS: Readonly<Record<SignupStep, string>> = {
  1: 'Create your account',
  2: 'About your organization',
  3: 'Accept the terms',
};

/**
 * Everything the wizard collects, camel-cased. Mapped onto the contract's wire
 * field names once, at submit time.
 */
export interface SignupWizardData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  /** The contracting entity (invoices, T&C 4.2) -> `company_legal_name`. */
  companyLegalName: string;
  /** ISO 3166-1 alpha-2, uppercase -> `country`. */
  country: string;
  /** Optional display name -> `workspace_name`, omitted from the payload when blank. */
  workspaceName: string;
  /** One of `COMPANY_SIZES` -> `company_size`, omitted when blank. */
  companySize: string;
  /** Required consent. Never pre-ticked. */
  acceptTerms: boolean;
  /** Optional marketing consent. Never pre-ticked, never bundled with the above. */
  acceptMarketing: boolean;
}

export const EMPTY_SIGNUP_WIZARD_DATA: SignupWizardData = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  companyLegalName: '',
  country: '',
  workspaceName: '',
  companySize: '',
  acceptTerms: false,
  acceptMarketing: false,
};

/** One message per field, plus a step-agnostic banner. */
export interface SignupWizardErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  companyLegalName?: string;
  country?: string;
  workspaceName?: string;
  companySize?: string;
  acceptTerms?: string;
  general?: string;
}

export type SignupErrorField = Exclude<keyof SignupWizardErrors, 'general'>;

/**
 * Text inputs and native selects share one name-keyed change handler, so the
 * host keeps a single `setData` call for the whole wizard.
 */
export type SignupFieldChangeHandler = (
  event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
) => void;

export type SignupConsentField = 'acceptTerms' | 'acceptMarketing';

export type SignupConsentHandler = (field: SignupConsentField, checked: boolean) => void;

/**
 * Profile decoded from a Google credential for DISPLAY ONLY — the server
 * re-verifies the id_token and derives the real identity from it.
 */
export interface GoogleProfilePrefill {
  email: string;
  firstName: string;
  lastName: string;
}

interface SignupErrorTarget {
  field: SignupErrorField;
  step: SignupStep;
}

/** Server wire field name -> the wizard field and the step that owns it. */
export const SIGNUP_FIELD_STEPS: Readonly<Record<string, SignupErrorTarget>> = {
  first_name: { field: 'firstName', step: 1 },
  last_name: { field: 'lastName', step: 1 },
  email: { field: 'email', step: 1 },
  password: { field: 'password', step: 1 },
  id_token: { field: 'email', step: 1 },
  company_legal_name: { field: 'companyLegalName', step: 2 },
  tenant_domain: { field: 'companyLegalName', step: 2 },
  country: { field: 'country', step: 2 },
  workspace_name: { field: 'workspaceName', step: 2 },
  tenant_name: { field: 'workspaceName', step: 2 },
  company_size: { field: 'companySize', step: 2 },
  accept_terms: { field: 'acceptTerms', step: 3 },
  accept_marketing: { field: 'acceptTerms', step: 3 },
};

/**
 * Error CODES from the envelope's `error` key -> the field they belong to.
 *
 * Note `DOMAIN_EXISTS`: the workspace domain is derived server-side from the
 * company legal name, so the conflict belongs on that field (step 2), NOT on
 * the email field, which is where every 409 used to land.
 */
const SIGNUP_ERROR_CODES: Readonly<Record<string, SignupErrorTarget>> = {
  TERMS_NOT_ACCEPTED: { field: 'acceptTerms', step: 3 },
  EMAIL_EXISTS: { field: 'email', step: 1 },
  // The backend emits AUTH_PROVIDER_MISMATCH (auth.py) — not PROVIDER_MISMATCH.
  AUTH_PROVIDER_MISMATCH: { field: 'email', step: 1 },
  DOMAIN_EXISTS: { field: 'companyLegalName', step: 2 },
};

/** Fallback copy when the server sends a code but no human-readable message. */
const SIGNUP_ERROR_CODE_MESSAGES: Readonly<Record<string, string>> = {
  TERMS_NOT_ACCEPTED:
    'You must accept the Terms & Conditions and the Privacy Policy to create an account.',
  EMAIL_EXISTS: 'An account with this email already exists.',
  AUTH_PROVIDER_MISMATCH:
    'This email is already registered with a password. Log in instead.',
  DOMAIN_EXISTS: 'That organization name is already taken. Please try a different one.',
};

/** The error envelope shape returned by `/auth/signup` and `/auth/google/signup`. */
export interface SignupApiErrorBody {
  error?: string;
  message?: string;
  errors?: Record<string, string[] | string>;
}

export interface SignupApiErrorResult {
  errors: SignupWizardErrors;
  /** Earliest step owning a mapped field, or null when only `general` was set. */
  step: SignupStep | null;
}

/**
 * Turn an API failure into per-field messages plus the step to jump back to.
 *
 * Field errors win over the generic banner, and when several steps are
 * implicated the user is sent to the EARLIEST one so they fix things in order.
 */
export const resolveSignupApiError = (
  status: number | undefined,
  body: SignupApiErrorBody | undefined
): SignupApiErrorResult => {
  const errors: SignupWizardErrors = {};
  const claimedSteps: SignupStep[] = [];

  const claim = (target: SignupErrorTarget, message: string) => {
    if (!errors[target.field]) errors[target.field] = message;
    claimedSteps.push(target.step);
  };

  Object.entries(body?.errors ?? {}).forEach(([wire, value]) => {
    const target = SIGNUP_FIELD_STEPS[wire];
    const message = Array.isArray(value) ? value[0] : value;
    if (target && message) claim(target, message);
  });

  const code = body?.error;
  if (code && SIGNUP_ERROR_CODES[code]) {
    claim(SIGNUP_ERROR_CODES[code], body?.message || SIGNUP_ERROR_CODE_MESSAGES[code]);
  }

  const step = claimedSteps.length > 0 ? (Math.min(...claimedSteps) as SignupStep) : null;

  if (step === null) {
    errors.general =
      body?.message ||
      (status === 429
        ? 'Too many attempts. Please try again later.'
        : 'An error occurred. Please try again.');
  }

  return { errors, step };
};
